/**
 * Mentor Gateway Debug Screen
 * 
 * Test mentorGateway by calling REAL production functions directly.
 * No wrapper layer - uses actual service methods the app uses.
 */

import React, { useState, useCallback } from 'react';
import { View, StyleSheet, ScrollView, Pressable } from 'react-native';
import { PageContainer, ContentContainer, Text, Card, Button, Input, Theme } from '../../components/ui';
import { auth } from '../../config/firebase';
import { PrivateProfileService } from '../../services/privateProfileService';
import { mentorChatService, MentorGatewayMessage, MentorChatService, UserProfile } from '../../services/mentorChatService';
import { 
  ChatPhase1Schema,
  ChatPhase2Schema,
} from '../../utils/mentorSchemas';

interface TestResult {
  success: boolean;
  data?: any;
  error?: string;
  duration: number;
}

const MentorGatewayDebugScreen: React.FC = () => {
  const [results, setResults] = useState<{ [key: string]: TestResult }>({});
  const [running, setRunning] = useState<string | null>(null);
  const [logs, setLogs] = useState<string[]>([]);

  // Editable inputs for scenarios
  const [chatMessage, setChatMessage] = useState('Can you recommend some books about productivity?');
  const [searchQuery, setSearchQuery] = useState('productivity time management focus');
  const [bookTitle, setBookTitle] = useState('Atomic Habits');
  const [bookAuthor, setBookAuthor] = useState('James Clear');

  const addLog = (msg: string) => {
    setLogs(prev => [`[${new Date().toLocaleTimeString()}] ${msg}`, ...prev.slice(0, 50)]);
  };

  const getUserProfile = async (): Promise<UserProfile> => {
    const user = auth.currentUser;
    if (!user) throw new Error('No authenticated user');

    const profileResult = await PrivateProfileService.getProfile(user.uid);
    if (!profileResult.success || !profileResult.data) {
      // Mock profile for testing
      return {
        uid: user.uid,
        languageCode: 'en',
        coreProfile: {
          goals: ['Improve leadership', 'Build team culture'],
          interests: ['Management', 'Communication'],
          challenges: ['Time management', 'Delegation'],
          personalityTraits: ['Analytical', 'Results-oriented'],
        },
        finishedContent: { bookIds: [], insightIds: [] },
      };
    }

    return MentorChatService.createUserProfile(profileResult.data);
  };

  const runTest = async (
    key: string,
    testFn: () => Promise<any>
  ) => {
    if (running) return;
    setRunning(key);
    addLog(`🚀 Starting: ${key}`);
    
    const start = Date.now();
    try {
      const data = await testFn();
      const duration = Date.now() - start;
      
      setResults(prev => ({
        ...prev,
        [key]: { success: true, data, duration },
      }));
      addLog(`✅ ${key} completed in ${duration}ms`);
    } catch (error) {
      const duration = Date.now() - start;
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      
      setResults(prev => ({
        ...prev,
        [key]: { success: false, error: errorMsg, duration },
      }));
      addLog(`❌ ${key} failed: ${errorMsg}`);
    } finally {
      setRunning(null);
    }
  };

  // TEST 1: Chat Phase 1
  const testChatPhase1 = useCallback(() => {
    runTest('chat_phase1', async () => {
      const userProfile = await getUserProfile();
      const messages: MentorGatewayMessage[] = [
        { role: 'user', content: chatMessage }
      ];
      
      return await mentorChatService.chatPhase1(
        messages,
        ChatPhase1Schema,
        { responseStyle: 'short', modelPreference: 'standard' },
        userProfile
      );
    });
  }, [chatMessage]);

  // TEST 2: Chat Phase 2
  const testChatPhase2 = useCallback(() => {
    runTest('chat_phase2', async () => {
      const userProfile = await getUserProfile();
      const messages: MentorGatewayMessage[] = [
        { role: 'user', content: chatMessage }
      ];
      
      return await mentorChatService.chatPhase2(
        messages,
        searchQuery,
        ChatPhase2Schema,
        { responseStyle: 'short', modelPreference: 'standard' },
        userProfile
      );
    });
  }, [chatMessage, searchQuery]);

  // TEST 3: Book Personalization (NEW)
  const testBookPersonalization = useCallback(() => {
    runTest('book_personalization', async () => {
      const user = auth.currentUser;
      if (!user) throw new Error('No authenticated user');

      const profileResult = await PrivateProfileService.getProfile(user.uid);
      if (!profileResult.success || !profileResult.data) {
        throw new Error('Profile not available');
      }

      const profile = profileResult.data;
      const userProfile = MentorChatService.createUserProfile(profile);
      const userName = profile.displayName?.split(' ')[0] || 'there';

      // Mock book context for testing (biggestTakeaway + key insights)
      const bookDescription = 'Atomic Habits reveals a systematic approach to building good habits and breaking bad ones. Learn the four laws of behavior change and discover how tiny improvements can lead to remarkable results over time. This book shows you exactly how to form habits that stick and how to overcome the obstacles that hold you back.';
      const keyInsights = [
        'The Power of Tiny Habits',
        'How Your Habits Shape Your Identity',
        'The Four Laws of Behavior Change',
        'Make It Obvious: The First Law',
        'Make It Attractive: The Second Law'
      ];

      return await mentorChatService.generateBookPersonalization(
        '107',
        bookTitle,
        bookAuthor,
        bookDescription,
        keyInsights,
        userProfile,
        userName
      );
    });
  }, [bookTitle, bookAuthor]);

  // TEST 4: Streaming
  const testStreaming = useCallback(() => {
    runTest('streaming', async () => {
      const userProfile = await getUserProfile();
      const messages: MentorGatewayMessage[] = [
        { role: 'user', content: chatMessage }
      ];

      let fullText = '';
      
      await mentorChatService.streamMentorGateway(
        messages,
        {
          onDelta: (delta) => { fullText += delta; },
          onComplete: () => {},
          onError: (error) => { throw new Error(error); },
        },
        undefined,
        { responseStyle: 'normal', modelPreference: 'standard' },
        userProfile
      );

      return { fullText, length: fullText.length };
    });
  }, [chatMessage]);

  const clearResults = () => {
    setResults({});
    setLogs([]);
  };

  return (
    <PageContainer>
      <ScrollView style={styles.container}>
        <ContentContainer>
          <Text variant="heading2" style={styles.title}>Mentor Gateway Debug</Text>
          
          <Text variant="body2" color="secondary" style={styles.subtitle}>
            Tests real production functions - exact same code paths as the app
          </Text>

          {/* Global Actions */}
          <View style={styles.actionsRow}>
            <Button
              title="Clear All"
              onPress={clearResults}
              disabled={!!running}
              style={styles.actionButton}
            />
          </View>

          {/* Test 1: Chat Phase 1 */}
          <Card style={styles.card}>
            <Text variant="heading3" style={styles.testTitle}>1. Chat Phase 1</Text>
            <Text variant="caption" color="secondary" style={styles.testDesc}>
              Get response + search query (~15-20s)
            </Text>
            
            <Text variant="caption" color="secondary" style={styles.inputLabel}>User Message:</Text>
            <Input
              value={chatMessage}
              onChangeText={setChatMessage}
              multiline
              style={styles.input}
            />
            
            <Button
              title={running === 'chat_phase1' ? 'Running...' : 'Run Test'}
              onPress={testChatPhase1}
              disabled={!!running}
              style={styles.testButton}
            />
            
            {results.chat_phase1 && (
              <View style={styles.resultBox}>
                <Text variant="caption" style={styles.resultStatus}>
                  {results.chat_phase1.success ? '✅ Success' : '❌ Error'} 
                  {' '}({results.chat_phase1.duration}ms)
                </Text>
                <Text style={styles.resultData}>
                  {results.chat_phase1.success 
                    ? JSON.stringify(results.chat_phase1.data, null, 2)
                    : results.chat_phase1.error
                  }
                </Text>
              </View>
            )}
          </Card>

          {/* Test 2: Chat Phase 2 */}
          <Card style={styles.card}>
            <Text variant="heading3" style={styles.testTitle}>2. Chat Phase 2</Text>
            <Text variant="caption" color="secondary" style={styles.testDesc}>
              Get recommendations using search query (~20-25s)
            </Text>
            
            <Text variant="caption" color="secondary" style={styles.inputLabel}>Search Query:</Text>
            <Input
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholder="e.g., leadership team management"
              style={styles.input}
            />
            
            <Button
              title={running === 'chat_phase2' ? 'Running...' : 'Run Test'}
              onPress={testChatPhase2}
              disabled={!!running}
              style={styles.testButton}
            />
            
            {results.chat_phase2 && (
              <View style={styles.resultBox}>
                <Text variant="caption" style={styles.resultStatus}>
                  {results.chat_phase2.success ? '✅ Success' : '❌ Error'} 
                  {' '}({results.chat_phase2.duration}ms)
                </Text>
                <Text style={styles.resultData}>
                  {results.chat_phase2.success 
                    ? JSON.stringify(results.chat_phase2.data, null, 2)
                    : results.chat_phase2.error
                  }
                </Text>
              </View>
            )}
          </Card>

          {/* Test 3: Book Personalization */}
          <Card style={styles.card}>
            <Text variant="heading3" style={styles.testTitle}>3. Book Personalization (NEW)</Text>
            <Text variant="caption" color="secondary" style={styles.testDesc}>
              Generate hyper-personalized book message (~10-15s)
            </Text>
            
            <Text variant="caption" color="secondary" style={styles.inputLabel}>Book Title:</Text>
            <Input
              value={bookTitle}
              onChangeText={setBookTitle}
              style={styles.input}
            />
            
            <Text variant="caption" color="secondary" style={styles.inputLabel}>Book Author:</Text>
            <Input
              value={bookAuthor}
              onChangeText={setBookAuthor}
              style={styles.input}
            />
            
            <Button
              title={running === 'book_personalization' ? 'Running...' : 'Run Test'}
              onPress={testBookPersonalization}
              disabled={!!running}
              style={styles.testButton}
            />
            
            {results.book_personalization && (
              <View style={styles.resultBox}>
                <Text variant="caption" style={styles.resultStatus}>
                  {results.book_personalization.success ? '✅ Success' : '❌ Error'} 
                  {' '}({results.book_personalization.duration}ms)
                </Text>
                <Text style={styles.resultData}>
                  {results.book_personalization.success 
                    ? JSON.stringify(results.book_personalization.data, null, 2)
                    : results.book_personalization.error
                  }
                </Text>
              </View>
            )}
          </Card>

          {/* Test 4: Streaming */}
          <Card style={styles.card}>
            <Text variant="heading3" style={styles.testTitle}>4. Streaming</Text>
            <Text variant="caption" color="secondary" style={styles.testDesc}>
              Stream text response with deltas (~15-20s)
            </Text>
            
            <Button
              title={running === 'streaming' ? 'Streaming...' : 'Run Test'}
              onPress={testStreaming}
              disabled={!!running}
              style={styles.testButton}
            />
            
            {results.streaming && (
              <View style={styles.resultBox}>
                <Text variant="caption" style={styles.resultStatus}>
                  {results.streaming.success ? '✅ Success' : '❌ Error'} 
                  {' '}({results.streaming.duration}ms)
                </Text>
                <Text style={styles.resultData}>
                  {results.streaming.success 
                    ? JSON.stringify(results.streaming.data, null, 2)
                    : results.streaming.error
                  }
                </Text>
              </View>
            )}
          </Card>

          {/* Logs */}
          {logs.length > 0 && (
            <Card style={styles.card}>
              <Text variant="heading3" style={styles.testTitle}>Activity Log</Text>
              <View style={styles.logsBox}>
                {logs.map((log, i) => (
                  <Text key={i} style={styles.logEntry}>{log}</Text>
                ))}
              </View>
            </Card>
          )}
        </ContentContainer>
      </ScrollView>
    </PageContainer>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  title: {
    marginBottom: Theme.spacing.sm,
    textAlign: 'center',
  },
  subtitle: {
    marginBottom: Theme.spacing.xl,
    textAlign: 'center',
    paddingHorizontal: Theme.spacing.lg,
  },
  actionsRow: {
    flexDirection: 'row',
    gap: Theme.spacing.md,
    marginBottom: Theme.spacing.lg,
  },
  actionButton: {
    flex: 1,
  },
  card: {
    marginBottom: Theme.spacing.lg,
  },
  testTitle: {
    marginBottom: Theme.spacing.xs,
    fontFamily: Theme.typography.fontFamily.semiBold,
  },
  testDesc: {
    marginBottom: Theme.spacing.md,
    fontSize: 12,
  },
  inputLabel: {
    marginTop: Theme.spacing.sm,
    marginBottom: Theme.spacing.xs,
  },
  input: {
    marginBottom: Theme.spacing.sm,
  },
  testButton: {
    marginTop: Theme.spacing.sm,
  },
  resultBox: {
    marginTop: Theme.spacing.md,
    padding: Theme.spacing.md,
    backgroundColor: Theme.colors.background,
    borderRadius: Theme.borderRadius.sm,
  },
  resultStatus: {
    marginBottom: Theme.spacing.sm,
    fontFamily: Theme.typography.fontFamily.medium,
  },
  resultData: {
    fontFamily: 'monospace',
    fontSize: 11,
    color: Theme.colors.textPrimary,
  },
  logsBox: {
    backgroundColor: Theme.colors.background,
    padding: Theme.spacing.sm,
    borderRadius: Theme.borderRadius.sm,
    maxHeight: 300,
  },
  logEntry: {
    fontFamily: 'monospace',
    fontSize: 11,
    color: Theme.colors.textSecondary,
    marginBottom: 2,
  },
});

export default MentorGatewayDebugScreen;
